import 'katex/dist/katex.min.css';
import { htmlAvecKatex } from './htmlAvecKatex';
import { fractionsEnProse } from '../../utils/fractionsProse';
import ImageElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/ImageElementEleve';
import NumberLineElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/NumberLineElementEleve';
import GeogebraElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/GeogebraElementEleve';
import ExpressionNumeriqueElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/ExpressionNumeriqueElementEleve';
import CompasInteractifElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/CompasInteractifElementEleve';
import RapporteurInteractifElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/RapporteurInteractifElementEleve';
import ZoomableMedia from './ZoomableMedia';

const MARKER_PATTERN = '\\{\\{element:(\\d+)\\}\\}';

/**
 * Formules KaTeX inline d'une chaîne HTML : `$\...$`, regex STRICTE (le `$`
 * d'ouverture suivi d'un `\`), cf. KATEX_STRICT_RE dans htmlAvecKatex.js.
 * Matches : `$\widehat{ABC}$`, `$\dfrac{1}{2}$` ; ignorés : `$5`, `$abc$`.
 */
/**
 * Rend un segment HTML avec ses formules `$\…$` en KaTeX, en une seule chaîne
 * (htmlAvecKatex.js) : le paragraphe n'est jamais coupé autour d'une formule.
 */
const renderHtmlWithKatex = (html, textClass) => (
    <span
        className={`rich-text-eleve break-words [&_.katex]:text-inherit ${textClass}`}
        dangerouslySetInnerHTML={{ __html: htmlAvecKatex(html) }}
    />
);

/**
 * Parse le texte avec markers {{element:N}} en segments alternés html/element.
 * Retourne { segments, usedIndices } pour savoir quels éléments ont été consommés.
 */
const parseSegments = (texte, elements) => {
  if (!texte || typeof texte !== 'string') return { segments: [], usedIndices: new Set() };

  const parts = [];
  const usedIndices = new Set();
  let lastIndex = 0;
  let match;
  const re = new RegExp(MARKER_PATTERN, 'g');

  while ((match = re.exec(texte)) !== null) {
    const before = texte.slice(lastIndex, match.index);
    if (before) {
      parts.push({ type: 'html', content: before });
    }
    const elIdx = parseInt(match[1], 10);
    if (elements && elements[elIdx]) {
      parts.push(elements[elIdx]);
      usedIndices.add(elIdx);
    }
    lastIndex = re.lastIndex;
  }

  const remaining = texte.slice(lastIndex);
  if (remaining) {
    parts.push({ type: 'html', content: remaining });
  }

  return { segments: parts, usedIndices };
};

/**
 * Rendu d'un élément additionnel côté élève.
 *
 * @param {Object} element - { type, config }
 * @param {string} theme - 'dark' | 'light'
 * @param {string} [imageMaxWidth] - override de config.maxWidth pour les images
 *   (ex : 'compact' pour l'aperçu prof). Si absent, la config de l'élément est utilisée telle quelle.
 */
const ElementRenderer = ({ element, theme, imageMaxWidth }) => {
  switch (element.type) {
    case 'image': {
      const cfg = imageMaxWidth ? { ...element.config, maxWidth: imageMaxWidth } : element.config;
      return <ImageElementEleve config={cfg} theme={theme} />;
    }
    case 'expression':
      return <ExpressionNumeriqueElementEleve config={element.config} theme={theme} />;
    case 'numberline':
      return <NumberLineElementEleve config={element.config} theme={theme} />;
    case 'geogebra':
      if (element.config?.ggb_svg) {
        return (
          <ZoomableMedia
            type="svg"
            content={element.config.ggb_svg}
            className="my-3 bg-white rounded-xl overflow-hidden border border-slate-600 w-fit max-w-full mx-auto [&_svg]:block [&_svg]:max-w-full [&_svg]:h-auto"
          />
        );
      }
      return <GeogebraElementEleve config={element.config} />;
    case 'compas_interactif':
      return <CompasInteractifElementEleve config={element.config} theme={theme} />;
    case 'rapporteur_interactif':
      return <RapporteurInteractifElementEleve config={element.config} theme={theme} />;
    default:
      return null;
  }
};

/**
 * Aperçu élève du bloc texte enrichi avec éléments intégrés.
 *
 * @param {string} texte - Texte HTML avec markers {{element:N}}
 * @param {Array} elements - Tableau d'éléments [{type, config}, ...]
 * @param {string} [theme='dark'] - 'dark' (fond sombre) ou 'light' (fond clair)
 * @param {string} [imageMaxWidth] - override de `config.maxWidth` pour les images additionnelles
 *   (ex : `'compact'` côté aperçu prof pour que les SVG tiennent dans une carte étroite).
 *   Non-propagé si undefined — comportement par défaut identique aux vues élève pleine largeur.
 */
const RichTextBlockEleve = ({ texte, elements, theme = 'dark', imageMaxWidth }) => {
  // Gère le cas où elements_additionnels est un string JSON
  const parsedElements = (() => {
    if (Array.isArray(elements)) return elements;
    if (!elements) return [];
    try {
      const data = typeof elements === 'string' ? JSON.parse(elements) : elements;
      return data?.elements || [];
    } catch { return []; }
  })();

  // Énoncé : une fraction seule dans une phrase passe en style texte, un
  // calcul garde sa grande fraction (utils/fractionsProse.js, 2026-09-06).
  const { segments, usedIndices } = parseSegments(fractionsEnProse(texte), parsedElements);

  // Éléments non consommés par les markers → affichés en bloc après le texte
  const remainingElements = parsedElements.filter((_, i) => !usedIndices.has(i));

  if (segments.length === 0 && remainingElements.length === 0) return null;

  // En thème clair (carte blanche), on ajoute le modificateur `rich-text-eleve--light`
  // pour que les h3/h4/tableaux (colorés en dur pour fond sombre) deviennent lisibles.
  const textClass = theme === 'dark' ? 'text-slate-100' : 'text-gray-800 rich-text-eleve--light';

  return (
    <div className="max-w-full break-words">
      {/* Segments inline (texte + expressions KaTeX) */}
      <div className="leading-relaxed" style={{ display: 'inline' }}>
        {segments.map((seg, idx) => {
          if (seg.type === 'html') {
            return <span key={idx}>{renderHtmlWithKatex(seg.content, textClass)}</span>;
          }

          // Expressions KaTeX → inline aligné avec le texte
          if (seg.type === 'expression') {
            return <span key={idx} className={`inline-block ${textClass} [&_.katex]:text-inherit`} style={{ verticalAlign: 'middle' }}><ElementRenderer element={seg} theme={theme} imageMaxWidth={imageMaxWidth} /></span>;
          }

          // Autres éléments (images, geogebra, numberline) → block
          return (
            <div key={idx} className="my-2">
              <ElementRenderer element={seg} theme={theme} imageMaxWidth={imageMaxWidth} />
            </div>
          );
        })}
      </div>

      {remainingElements.length > 0 && (
        <div className="space-y-3">
          {remainingElements.map((el, idx) => (
            <ElementRenderer key={`rem-${idx}`} element={el} theme={theme} imageMaxWidth={imageMaxWidth} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RichTextBlockEleve;
